import { ChatViewer } from '@core/chat';
import { PresentationSlides } from '@core/presentation-panel';
import { ClientType, LiveStatus, ParticipantAnswers, ParticipantType } from '@core/types';
import { ErrorMessagePage, Loader, MaintenanceNotice, MobileMenuNavigator, useLayout } from '@core/ui';
import { Chapters } from '@online-webinar/chapters';
import {
    ChapterWithTimestamp,
    onDemandCurrentVideoTimeSeeded,
    onDemandJoinWebinar,
    onDemandLeaveWebinar,
    parseTimespan,
    useAppDispatch,
    useAppSelector
} from '@online-webinar/store';
import { useEffect, useRef, useState } from 'react';
import { onExamClose, showSurveyOrExam } from '../../../hooks';
import { computeResumeStart } from '../../../utils/computeResumeStart';
import { isAutoHash } from '../../../utils/onDemandAutoHash';
import { markCompleted, readResume } from '../../../utils/onDemandResume';
import { ChapterContent } from '../../molecules/ChapterContent';
import { ChatContent } from '../../molecules/ChatContent';
import { PresentationContent } from '../../molecules/PresentationContent';
import { TestWebinarOverlay } from '../../molecules/TestWebinarOverlay';
import { SeekRequest, VideoOnDemand } from '../../molecules/VideoOnDemand';
import { PollContainer } from '../../organism/PollContainer';
import { QuestionnaireOverlay } from '../../organism/QuestionnaireOverlay';
import { ViewerLiveWebinarLayout } from '../../organism/ViewerLiveWebinarLayout';
import { ConnectionOfflineOverlay } from '../ConnectionOfflineOverlay';

const emptyAnswers = { isFinalized: false, answers: [] } as ParticipantAnswers;
export const ViewOnDemandWebinarOrganism = ({
    webinarId,
    linkCode,
    anonymousId,
    anonymousAuthorizationCode
}: {
    webinarId: string;
    linkCode?: string;
    anonymousId?: string;
    anonymousAuthorizationCode?: string;
}) => {
    const { mainContainerRef, chapterActivated, chatActivated, slidesActivated, onMobileMenuItemClicked, gridContentStyles, isLandscape, mobileMenuEnabled } =
        useLayout();

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(
            onDemandJoinWebinar({
                webinarId: webinarId,
                linkCode: linkCode,
                anonymousId: anonymousId,
                anonymousAuthorizationCode: anonymousAuthorizationCode,
                clientType: ClientType.on_demand
            })
        );
        return () => {
            dispatch(onDemandLeaveWebinar());
        };
    }, [webinarId, linkCode, dispatch]);

    const isInitialized = useAppSelector((state) => state.onDemandWebinar.isInitialized);
    // The store's own idea of which webinar it currently holds data for. See the resolution effect
    // below for why this is needed in addition to the webinarId prop.
    const storeWebinarId = useAppSelector((state) => state.onDemandWebinar.webinarId);
    const dataConnection = useAppSelector((state) => state.onDemandWebinar.dataConnection);
    const onDemandData = useAppSelector((state) => state.onDemandWebinar.onDemandData);
    const isTest = useAppSelector((state) => state.onDemandWebinar.webinarData?.isTest);
    const slideDeck = useAppSelector((state) => state.onDemandWebinar.slideDeck);
    const activeSlideNumber = useAppSelector((state) => state.onDemandWebinar.activeSlideNumber);
    const chapters = useAppSelector((state) => state.onDemandWebinar.chapters);
    const activeChapter = useAppSelector((state) => state.onDemandWebinar.activeChapter);
    const unreadChatMessages = useAppSelector((state) => state.onDemandWebinar.unreadChatMessages);
    const survey = useAppSelector((state) => state.onDemandWebinar.survey);
    const surveyAnswers = useAppSelector((state) => state.onDemandWebinar.surveyAnswers);
    const exam = useAppSelector((state) => state.onDemandWebinar.exam);
    const examAnswers = useAppSelector((state) => state.onDemandWebinar.examAnswers);
    const videoLayout = useAppSelector((state) => state.onDemandWebinar.videoLayout);
    const resumePosition = useAppSelector((state) => state.onDemandWebinar.resumePosition);
    const resumeCompleted = useAppSelector((state) => state.onDemandWebinar.resumeCompleted);
    const timeline = useAppSelector((state) => state.onDemandWebinar.timeline);
    const actualDuration = useAppSelector((state) => state.onDemandWebinar.webinarData?.actualDuration);
    // Scopes the local resume fallback per-viewer so a shared device doesn't let one account inherit
    // another's position — see the comment on onDemandResume's key(). Only for identified viewers:
    // an anonymous participant is re-minted every browser session (its id traces back to AnonymousId,
    // which ViewAnonymousPage stores in sessionStorage — cleared per session), so scoping by it would
    // make the anonymous fallback miss on every single visit, defeating the one thing it exists for.
    // Anonymous viewers keep the original per-webinar-only key.
    const me = useAppSelector((state) => state.onDemandWebinar.me);
    const participantId = me && me.participantType !== ParticipantType.anonymous ? me.participantId : undefined;

    const [hasChapters, setHasChapters] = useState(false);
    const [hasSlides, setHasSlides] = useState(false);
    // A seek is a one-shot request, not a position: the viewer can scrub away without `seek` hearing
    // about it, so asking for the same position again (same chapter clicked twice, same deep-link
    // re-opened) has to seek again. A plain number cannot express that — React drops the identical
    // state update and nothing moves. The token makes each request distinct.
    const [seek, setSeek] = useState<SeekRequest>({ time: 0, token: 0 });
    const requestSeek = (time: number) => setSeek((previous) => ({ time, token: previous.token + 1 }));
    const [resume, setResume] = useState<{ startPosition?: number; resolved: boolean }>({
        resolved: false
    });
    // Reset resolution when webinarId itself changes — e.g. navigating from one on-demand straight to
    // another without a full remount (the join/leave effect above already handles rejoining). Without
    // this, resume.resolved stays true from the PREVIOUS webinar, and the resolution effect's own
    // guard (`if (!isInitialized || resume.resolved) return`) then skips recomputing entirely for the
    // new one, leaving resume.startPosition frozen at whatever the previous webinar resolved to.
    // Adjusting state during render (rather than in an effect) avoids a transient render where the
    // stale startPosition would otherwise reach VideoOnDemand before the reset takes effect.
    //
    // This alone is NOT sufficient — see the storeWebinarId check in the resolution effect below for
    // the other half of this fix.
    const [resumeWebinarId, setResumeWebinarId] = useState(webinarId);
    if (webinarId !== resumeWebinarId) {
        setResumeWebinarId(webinarId);
        setResume({ resolved: false });
    }

    // The skip check below needs the *current* active chapter, but we must NOT re-run the seek effect
    // when it changes: ViewOnDemandWebinarPage rewrites the hash (via replaceState) on every active
    // chapter change, and reacting to activeChapter here would race that write. A ref gives us the
    // latest value without adding it to the effect deps.
    const activeChapterRef = useRef(activeChapter);
    activeChapterRef.current = activeChapter;

    useEffect(() => {
        setHasChapters(chapters?.length !== 0);
    }, [chapters]);

    // Resolve the resume position exactly once, as soon as the webinar payload + timeline are in.
    //
    // storeWebinarId !== webinarId guards against a race on a same-instance webinar switch (A -> B):
    // this effect and the join/leave effect above both fire in the SAME commit, but the join/leave
    // effect runs first and its dispatches (leaving A, joining B) don't retroactively change what THIS
    // render already captured via useAppSelector — chapters/timeline/resumePosition/isInitialized here
    // are still A's, even though the store itself has already moved on to B by the time this line runs.
    // isInitialized can't catch that (it's equally stale), but storeWebinarId can: onDemandJoinWebinar's
    // reducer sets it synchronously, so on this exact pass storeWebinarId (also captured from A's stale
    // render) legitimately still reads as A while webinarId (the prop) already reads as B — a mismatch
    // that only exists in this one transitional pass, and correctly signals "the store hasn't produced
    // a fresh render for B yet, don't resolve from what's here." The very next render (once B's data
    // arrives) has both agreeing again, and resolution proceeds normally.
    useEffect(() => {
        if (!isInitialized || resume.resolved || storeWebinarId !== webinarId) return;

        try {
            const hash = window.location.hash.slice(1);
            const parts = hash ? hash.split('&') : [];
            const chapterPart = parts.find((p) => !p.startsWith('t='));
            const decodedChapterTitle = chapterPart ? decodeURIComponent(chapterPart) : undefined;
            // A chapter hash that exactly matches what THIS tab last auto-wrote (via markAutoHash in
            // ViewOnDemandWebinarPage) is our own bookkeeping, not a real deep-link — it must not
            // suppress resume, or reloading a chaptered on-demand would always land back at whatever
            // chapter you were in instead of your actual position. A #t= is never auto-written, so it
            // always counts as a real deep-link.
            const hasHashSeek =
                parts.some((p) => p.startsWith('t=')) ||
                (!!decodedChapterTitle &&
                    chapters.some((c) => c.chapterTitle === decodedChapterTitle) &&
                    !isAutoHash(webinarId, decodedChapterTitle));

            const chapterStarts = (timeline ?? [])
                .filter((event) => event.eventType === 'ChapterChangedEvent')
                .map((event) => parseTimespan(event.timestamp));

            const local = readResume(webinarId, participantId);
            const { startPosition, markLocalCompleted } = computeResumeStart({
                hasHashSeek,
                serverResumePosition: resumePosition,
                serverCompleted: resumeCompleted,
                local,
                chapterStarts,
                durationSeconds: parseTimespan(actualDuration ?? '')
            });

            if (markLocalCompleted) markCompleted(webinarId, participantId);

            if (startPosition !== undefined) {
                requestSeek(startPosition);
                // Seed only — the server already told us this position; re-reporting it via
                // onDemandCurrentVideoTimeUpdated would create/reset a session the viewer isn't
                // actually watching right now.
                dispatch(onDemandCurrentVideoTimeSeeded(startPosition));
            }

            setResume({ startPosition, resolved: true });
        } catch {
            setResume({ startPosition: undefined, resolved: true });
        }
    }, [
        isInitialized,
        resume.resolved,
        storeWebinarId,
        chapters,
        timeline,
        resumePosition,
        resumeCompleted,
        actualDuration,
        webinarId,
        participantId,
        dispatch
    ]);

    // Seek to whatever the URL hash specifies — on load and whenever the hash changes.
    // Supports: #<chapterTitle>, #t=<seconds>, or #<chapterTitle>&t=<seconds> (t= overrides chapter).
    // Skip when the hash only points at the chapter we're already playing (and there's no explicit
    // t= override): otherwise a hash update for the current chapter would yank playback to its start.
    //
    // The chapter branch's activeChapterRef check only guards the chapter case; a #t= has no equivalent
    // "we're already there" signal to compare against. Without appliedHashSeekRef, this effect re-running
    // for any OTHER reason it depends on (isInitialized toggling on a reconnect, chapters updating) would
    // re-apply the SAME still-present #t= every time — yanking the viewer back to that timestamp and
    // regressing the server position — even though nothing about the hash itself actually changed.
    const appliedHashSeekRef = useRef<string | null>(null);
    useEffect(() => {
        if (!isInitialized) return;

        const applyHashSeek = () => {
            const hash = window.location.hash.slice(1);
            if (!hash || appliedHashSeekRef.current === hash) return;

            // decodeURIComponent throws on a malformed percent-encoding (a stray "%", a truncated
            // "%XX") — reachable from a hand-edited URL, a mangled share link, or a paste that cut a
            // sequence in half. This effect calls applyHashSeek() synchronously and unconditionally on
            // every mount/reconnect (see below), so an uncaught throw here would surface well past
            // anything the resume-resolution effect's own try/catch protects.
            try {
                const parts = hash.split('&');
                const chapterPart = parts.find((p) => !p.startsWith('t='));
                const timePart = parts.find((p) => p.startsWith('t='));

                let seekTime: number | undefined;

                if (chapterPart) {
                    const title = decodeURIComponent(chapterPart);
                    // Same reasoning as hasHashSeek in the resume-resolution effect above: a chapter hash
                    // this tab auto-wrote (via markAutoHash) is our own bookkeeping, not a real seek
                    // request. Without this check, on initial load activeChapterRef.current is still one
                    // render behind the resume effect's dispatch (it's only synced in the render body), so
                    // this guard alone can't yet see that the hash already names the resumed-to chapter —
                    // and would yank playback back to that chapter's start right after resume sets it.
                    if (!timePart && (activeChapterRef.current?.chapterTitle === title || isAutoHash(webinarId, title))) return;
                    const chapter = chapters.find((c) => c.chapterTitle === title);
                    if (chapter) seekTime = chapter.timestamp;
                }

                if (timePart) {
                    const t = parseFloat(timePart.slice(2));
                    if (!isNaN(t)) seekTime = t;
                }

                if (seekTime !== undefined) {
                    appliedHashSeekRef.current = hash;
                    requestSeek(seekTime);
                }
            } catch {
                /* malformed hash — nothing to seek to, leave playback where it is */
            }
        };

        applyHashSeek();
        window.addEventListener('hashchange', applyHashSeek);
        return () => window.removeEventListener('hashchange', applyHashSeek);
    }, [isInitialized, chapters]);

    useEffect(() => {
        setHasSlides(slideDeck?.slides.length !== 0);
    }, [slideDeck]);

    if (dataConnection.status === LiveStatus.Error) {
        const error = dataConnection.error ?? { statusCode: 500, success: false };
        return <ErrorMessagePage apiError={error} />;
    }

    // Gated on resume.resolved alone, not isInitialized: the resume effect above only runs once
    // isInitialized is true, so resume.resolved can never become true before it — this still shows
    // the Loader for the initial load. But isInitialized flips false->true again on EVERY SignalR
    // reconnect (OnConnectedAsync re-sends OnDemandState), and gating on it here too would unmount
    // and remount VideoOnDemand on every one of those — restarting playback from the frozen
    // resume.startPosition and, via its very first tracking write, silently overwriting real
    // progress with that stale position. A genuine videoUrl change still reaches HlsPlayer, which
    // reacts to it via its own [config, src, autoPlay] effect without needing a full remount here.
    if (!resume.resolved) {
        return <Loader classNames="m-auto" />;
    }

    return (
        <ViewerLiveWebinarLayout
            ref={mainContainerRef}
            isLandscape={isLandscape}
            isMobileEnabled={mobileMenuEnabled}
            banner={<MaintenanceNotice area="on-demand" gatewayOverlay={false} />}
        >
            <ConnectionOfflineOverlay liveStatus={dataConnection.status} />
            <QuestionnaireOverlay onClose={(type) => onExamClose(dispatch, type, survey, surveyAnswers || emptyAnswers)} />
            <VideoOnDemand
                isLandscape={isLandscape}
                isMobile={mobileMenuEnabled}
                videoLayout={videoLayout}
                videoUrl={onDemandData?.videoUrl ?? ''}
                seek={seek}
                startPosition={resume.startPosition}
                onEnded={() => {
                    showSurveyOrExam(dispatch, survey, surveyAnswers || emptyAnswers, exam, examAnswers || emptyAnswers);
                }}
                webinarId={webinarId}
                participantId={participantId}
            />
            <TestWebinarOverlay isTest={isTest} />
            {slideDeck?.slides.length !== 0 && (slidesActivated || !mobileMenuEnabled) ? (
                <PresentationContent isLandscape={isLandscape} isMobile={mobileMenuEnabled} videoLayout={videoLayout}>
                    <PresentationSlides
                        slides={slideDeck?.slides ?? []}
                        currentSlideNumber={activeSlideNumber ?? 0}
                        classNames={!isLandscape ? 'object-top' : ''}
                        showFullScreen="true"
                        isMobile={mobileMenuEnabled}
                        videoLayout={videoLayout}
                    />
                </PresentationContent>
            ) : null}
            <div
                className={`${mobileMenuEnabled ? (isLandscape ? 'row-start-1 col-start-1' : 'row-start-2 col-start-1') : 'row-span-full col-start-2'} grid grid-rows-[auto_1fr] min-h-0`}
            >
                {chapterActivated || !mobileMenuEnabled ? (
                    <ChapterContent isLandscape={isLandscape} isMobile={mobileMenuEnabled}>
                        {hasChapters && (
                            <Chapters
                                chapters={chapters}
                                activeChapter={activeChapter}
                                onChapterClick={(chapter) => requestSeek((chapter as ChapterWithTimestamp).timestamp)}
                                showHeader={mobileMenuEnabled ? false : true}
                            />
                        )}
                    </ChapterContent>
                ) : null}
                {chatActivated || !mobileMenuEnabled ? (
                    <ChatContent isMobile={mobileMenuEnabled}>
                        <ChatViewer isOnDemand />
                    </ChatContent>
                ) : null}
            </div>
            <PollContainer isOnDemand={true} isLandscape={isLandscape} mobileMenuEnabled={mobileMenuEnabled} />
            {mobileMenuEnabled ? (
                <MobileMenuNavigator
                    onItemClick={onMobileMenuItemClicked}
                    hasSlides={hasSlides}
                    activeSlideNumber={activeSlideNumber}
                    hasChapters={hasChapters}
                    isLandscape={isLandscape}
                    unreadChatMessages={unreadChatMessages}
                />
            ) : null}
        </ViewerLiveWebinarLayout>
    );
};
